/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ResetPasswordPage
 *
 * Reads uid, token, and user_type from the URL and wires ResetPasswordForm
 * to the backend ResetPasswordAPIView.
 *
 * URL shape:
 *   /reset-password?uid=...&token=...&type=admin
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ResetPasswordForm from "../components/ResetPasswordForm";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import AlertDialog from "../components/common/AlertDialog";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const RESET_PASSWORD_ENDPOINT = "/arcee/auth/password/reset/";

function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState("admin");

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");
  const [fatalError, setFatalError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qUid = params.get("uid");
    const qToken = params.get("token");
    const qType = params.get("type") || "admin";

    setUid(qUid);
    setToken(qToken);
    setUserType(qType === "application" ? "application" : "admin");

    if (!qUid || !qToken) {
      setFatalError(true);
      setAlertTitle("Invalid link");
      setAlertMessage("The password reset link is invalid or incomplete. Please request a new link.");
      setAlertType("error");
      setAlertOpen(true);
    }
  }, [location.search]);

  const handleResetPassword = async (form, setErrors) => {
    if (!uid || !token) {
      setErrors({
        new_password: "Reset link is invalid or missing. Please request a new link.",
      });
      return;
    }

    try {
      setErrors({});

      const resp = await apiHelper.apiCall(RESET_PASSWORD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: uid,
          token: token,
          user_type: userType,
          new_password: form.new_password,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      const message =
        (data && (data.message || data.error)) ||
        "Something went wrong. Please try again.";

      // Handle invalid/expired/unauthorized token cases
      if (
        resp.status === 401 ||
        resp.status === 403 ||
        (data?.error && typeof data.error === "string" && (
          data.error.toLowerCase().includes("invalid") ||
          data.error.toLowerCase().includes("expired") ||
          data.error.toLowerCase().includes("token")
        ))
      ) {
        setAlertTitle("Link Expired");
        setAlertMessage("This reset link is no longer valid. Please try again later.");
        setAlertType("error");
        setAlertOpen(true);
        setFatalError(true);
        return;
      }

      if (!resp.ok || data?.error) {
        // token invalid / expired / other backend errors
        setErrors({
          new_password: message,
        });
        return;
      }

      setAlertTitle("Password Reset");
      setAlertMessage(message || "Your password has been reset successfully.");
      setAlertType("success");
      setAlertOpen(true);
      setFatalError(false);
    } catch (err) {
      // Detect 401 or OAuth unauthorized inside APIHelper error
      const msg = String(err?.message || "").toLowerCase();

      if (
        msg.includes("401") ||
        msg.includes("unauthorized") ||
        msg.includes("invalid token") ||
        msg.includes("credentials") ||
        msg.includes("authentication") ||
        msg.includes("expired")
      ) {
        setAlertTitle("Link Expired");
        setAlertMessage("This reset link is no longer valid. Please try again later.");
        setAlertType("error");
        setAlertOpen(true);
        setFatalError(true);
        return;
      }

      setErrors({
        new_password: "Unable to reset password. Please try again.",
      });
    }
  };

  const handleAlertOk = () => {
    setAlertOpen(false);
    if (fatalError) {
      navigate("/forgot-password");
    } else {
      navigate("/sign-in");
    }
  };

  return (
    <>
      <ResetPasswordForm onSubmit={handleResetPassword} />

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={handleAlertOk}
        onCancel={() => setAlertOpen(false)}
        mini_mode={false}
      />
    </>
  );
}

export default ResetPasswordPage;
